/** "AI 초안 쓰기": have the AI write drafts for one campaign now, without
 *  waiting for the campaign's cron. One topic becomes one draft per enabled
 *  platform (per angle, when it is spread over angles). The drafts land in
 *  검토; nothing is scheduled or posted until someone approves them there.
 *
 *  The call waits for the model, so the dialog says it takes up to a minute
 *  and says up front how many drafts to expect. */

import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api, ApiError, type Campaign, type GenerateNowResult } from '@/api/client'
import { useInvalidatePosts } from '@/lib/postActions'
import { useCampaigns } from '@/lib/queries'
import { Button, Dialog, ErrorState, Field, FieldGroup, Input, Select, Textarea, platformName, useToast } from '@/ui'
import { enabledPlatforms } from '../campaigns/shared'

/** The raw text the server kept for a failure, shown behind a toggle. */
function Details({ text }: { text?: string }) {
  if (!text) return null
  return (
    <details className="mt-2 text-meta text-muted-foreground">
      <summary className="cursor-pointer select-none">자세히</summary>
      <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-md border border-border bg-muted px-2 py-1.5 font-mono text-micro">
        {text}
      </pre>
    </details>
  )
}

const detailOf = (e: unknown) => (e instanceof ApiError ? (e.body as { detail?: string } | null)?.detail : undefined)

/** How many drafts one run writes: one per enabled platform, and per angle
 *  when the topic is spread over angles. */
export function draftCount(platforms: number, expand: boolean, angles: number): number {
  return platforms * (expand ? Math.max(1, angles) : 1)
}

/** What the toast says when a run ends. */
export function resultLine(r: Pick<GenerateNowResult, 'drafts' | 'judge_rejected' | 'failed'>): string {
  const parts: string[] = []
  parts.push(r.drafts > 0 ? `초안 ${r.drafts}개를 검토에 올렸다` : '검토에 올린 초안은 없다')
  if (r.judge_rejected > 0) parts.push(`광고 판정에서 ${r.judge_rejected}개가 떨어져 심사 탈락에 있다`)
  if (r.failed > 0) parts.push(`${r.failed}곳은 쓰지 못했다`)
  return parts.join(' — ')
}

function PlatformsLine({ c }: { c: Campaign }) {
  const on = enabledPlatforms(c)
  return <>{on.map(platformName).join(', ')}</>
}

export function GenerateNowDialog({
  open,
  onClose,
  initialCampaignId,
}: {
  open: boolean
  onClose: () => void
  initialCampaignId?: number
}) {
  // A Coupang campaign writes only from signals; there is nothing to ask it for.
  const campaigns = (useCampaigns().data?.items ?? []).filter((c) => c.Mode !== 'affiliate')
  const invalidate = useInvalidatePosts()
  const toast = useToast()
  const [campaignId, setCampaignId] = useState(0)
  const [topic, setTopic] = useState('')
  const [expand, setExpand] = useState(false)
  const [angles, setAngles] = useState(3)
  const [result, setResult] = useState<GenerateNowResult | null>(null)

  useEffect(() => {
    if (!open || campaignId || campaigns.length === 0) return
    const pick = campaigns.find((c) => c.ID === initialCampaignId) ?? campaigns.find((c) => c.Status === 'active') ?? campaigns[0]
    setCampaignId(pick.ID)
    setAngles(pick.TopicAnglesCount ?? 3)
  }, [open, campaigns.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const campaign = campaigns.find((c) => c.ID === campaignId)
  const topicMode = campaign?.Mode === 'topic'
  const pool = campaign?.TopicPool ?? []
  const nextInPool = pool.length > 0 ? pool[(campaign?.TopicRotationState ?? 0) % pool.length] : ''
  const platforms = campaign ? enabledPlatforms(campaign) : []
  const paused = campaign?.Status === 'paused'
  const count = draftCount(platforms.length, expand, angles)
  const run = useMutation({
    mutationFn: () =>
      api.generateNow(campaignId, { topic: topic.trim() || undefined, expand, angles_count: expand ? angles : undefined }),
    onSuccess: (r) => {
      setResult(r)
      const line = resultLine(r)
      if (r.failed > 0 || r.drafts === 0) toast.error(line)
      else toast.success(line)
    },
    // A run that failed may still have moved the topic pool or written some
    // drafts; either way the lists are stale.
    onSettled: () => void invalidate(),
  })

  const close = () => {
    if (run.isPending) return
    setResult(null)
    setTopic('')
    run.reset()
    onClose()
  }

  const noTopic = !topic.trim() && !(topicMode && pool.length > 0)
  const blocked = !campaignId || paused || platforms.length === 0 || noTopic

  return (
    <Dialog
      open={open}
      onClose={close}
      title="AI 초안 쓰기"
      size="lg"
      description="주제 하나를 AI가 글로 쓴다. 쓴 글은 검토에 올라가고, 예약도 게시도 하지 않는다. 승인은 검토에서 한다."
      footer={
        <>
          <Button onClick={close} disabled={run.isPending}>
            {result ? '닫기' : '취소'}
          </Button>
          {!result && (
            <Button variant="primary" loading={run.isPending} disabled={blocked} onClick={() => run.mutate()}>
              {run.isPending ? '쓰는 중' : '초안 쓰기'}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        <FieldGroup columns={2}>
          <Field label="캠페인" required>
            <Select
              value={String(campaignId)}
              onChange={(e) => {
                const next = campaigns.find((c) => c.ID === Number(e.target.value))
                setCampaignId(Number(e.target.value))
                if (next) setAngles(next.TopicAnglesCount ?? 3)
              }}
            >
              {campaigns.length === 0 && <option value="0">— 캠페인이 아직 없다 —</option>}
              {campaigns.map((c) => (
                <option key={c.ID} value={String(c.ID)}>
                  {c.Name}
                  {c.Status === 'paused' ? ' (멈춤)' : ''}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="각도로 넓히기"
            hint={expand ? undefined : '주제를 여러 각도로 나눠 글을 더 만든다.'}
            aside={
              <label className="flex items-center gap-1.5 text-table text-foreground">
                <input
                  type="checkbox"
                  checked={expand}
                  onChange={(e) => setExpand(e.target.checked)}
                  className="h-4 w-4 accent-primary"
                />
                사용
              </label>
            }
          >
            <Input
              type="number"
              min={2}
              max={8}
              value={angles}
              disabled={!expand}
              aria-label="각도 수"
              onChange={(e) => setAngles(Number(e.target.value))}
            />
          </Field>
        </FieldGroup>
        <Field
          label="주제"
          required={!topicMode || pool.length === 0}
          hint={
            topicMode && nextInPool
              ? `비우면 주제 풀의 다음 항목(“${nextInPool}”)을 쓰고, 풀이 한 칸 넘어간다.`
              : undefined
          }
        >
          <Textarea
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            rows={3}
            placeholder="예: AI 코딩 도구로 1인 개발 생산성 높이는 법"
          />
        </Field>

        {campaign && (
          <ul className="space-y-1 rounded-md border border-border bg-muted px-3 py-2.5 text-table" aria-label="이번에 일어나는 일">
            {paused ? (
              <li className="text-status-failed">멈춘 캠페인이다 — 재개한 뒤 쓴다.</li>
            ) : platforms.length === 0 ? (
              <li className="text-status-failed">켜진 플랫폼이 없다 — 캠페인에서 플랫폼을 켠 뒤 쓴다.</li>
            ) : (
              <li>
                초안 <b>{count}개</b>를 쓴다. 켜진 플랫폼 {platforms.length}개(<PlatformsLine c={campaign} />)
                {expand ? `에 각도 ${Math.max(1, angles)}개를 곱한 수다.` : '마다 하나씩이다.'}
              </li>
            )}
            <li>쓴 글은 검토 대기에 올라간다. 예약도 게시도 하지 않는다.</li>
            <li>AI를 부르는 일이라 최대 1분쯤 걸린다. 끝날 때까지 이 창을 연 채로 둔다.</li>
            {campaign.Mode === 'manual' && <li>수동 캠페인은 자동으로는 글을 쓰지 않는다. 여기서는 적은 주제로 한 번 쓴다.</li>}
          </ul>
        )}

        {run.error != null && (
          <div>
            <ErrorState title="초안을 쓰지 못했다" error={run.error} />
            <Details text={detailOf(run.error)} />
          </div>
        )}
        {result && (
          <div className="rounded-md border border-border bg-muted px-3 py-2 text-table">
            <p>
              {resultLine(result)}. 주제: {result.topic}
              {result.expanded && ' · 각도로 넓혔다'}
            </p>
            {result.skipped > 0 && <p className="mt-1">앞 각도가 모두 실패해 나머지 {result.skipped}개 각도는 쓰지 않았다.</p>}
            <Details text={result.detail} />
          </div>
        )}
      </div>
    </Dialog>
  )
}
